import { useApi } from '@/common/ApiHelper';
import type { SalaryStructureReviewPoint } from '@/components/analysis/types';
import { isHourlySalaryType } from '@/composables/analysis/salaryByType';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { ConditionOperator, LogicalOperator } from '@sysarb/sysarb-api/models/common/selection/Selection';
import type { Guid } from 'guid-typescript';
import type { QueryOptions } from '@tanstack/vue-query';
import { queryOptions } from '@tanstack/vue-query';

type SalaryReviewEmploymentsOptions = {
    queries: {
        /** Salary-structure scatter points for the current manager's node, scoped to a salary review. */
        getSalaryReviewEmployments: (salaryReviewId: Guid | string | null) => QueryOptions<SalaryStructureReviewPoint[], Error>;
    };
};

export const salaryReviewEmployments: SalaryReviewEmploymentsOptions = {
    queries: {
        getSalaryReviewEmployments: (salaryReviewId: Guid | string | null): QueryOptions<SalaryStructureReviewPoint[], Error> => {
            const { selectedInstance, currentOrganisationNodeId } = useLoginStore();
            const managerIdValue = selectedInstance?.user?.Manager_ID ?? null;
            const nodeId = currentOrganisationNodeId;

            return queryOptions<SalaryStructureReviewPoint[], Error>({
                queryKey: ['manager', 'salary-review-employments', nodeId, managerIdValue, salaryReviewId?.toString() ?? null],
                queryFn: async (): Promise<SalaryStructureReviewPoint[]> => {
                    const result = await useApi().salaryReviewEmployment.getBySelection({
                        Condition: {
                            LogicalOperator: LogicalOperator.And,
                            Conditions: [
                                {
                                    PropertyName: 'SalaryReview_ID',
                                    ConditionOperator: ConditionOperator.EqualTo,
                                    Value: salaryReviewId,
                                },
                                {
                                    PropertyName: 'OrganisationNode_ID',
                                    ConditionOperator: ConditionOperator.EqualTo,
                                    Value: nodeId,
                                },
                                {
                                    LogicalOperator: LogicalOperator.Or,
                                    Conditions: [
                                        {
                                            PropertyName: 'Manager_ID',
                                            ConditionOperator: ConditionOperator.EqualTo,
                                            Value: managerIdValue,
                                        },
                                        {
                                            PropertyName: 'ManagerDeputy',
                                            ConditionOperator: ConditionOperator.Contains,
                                            Value: managerIdValue,
                                        },
                                    ],
                                },
                            ],
                        },
                    });

                    return result.map(reviewEmployment => {
                        const hourly = isHourlySalaryType(reviewEmployment.salaryType.value);
                        return {
                            id: reviewEmployment.id?.toString() ?? '',
                            employmentId: reviewEmployment.employment.id.toString(),
                            organisationNodeId: reviewEmployment.organisationNode.id.toString(),
                            employeeName: reviewEmployment.employee.name,
                            assessmentPercentage: reviewEmployment.assessmentResultPercentage.numerator ?? 0,
                            totalSalary: hourly ? reviewEmployment.hourlySalary : reviewEmployment.totalSalary,
                            newTotalSalary: hourly ? reviewEmployment.newHourlySalary : reviewEmployment.newTotalSalary,
                            work: reviewEmployment.salaryReviewWork.name,
                            position: reviewEmployment.position,
                            employmentDate: reviewEmployment.employmentDate,
                            newSalaryIncrement: reviewEmployment.newSalaryIncrement,
                            newSalaryIncrementSecond: reviewEmployment.newSalaryIncrementSecond,
                            newSalaryExchange: reviewEmployment.newSalaryExchange,
                            gender: reviewEmployment.gender.value,
                            salaryYear: reviewEmployment.salaryYear,
                            formOfEmployment: reviewEmployment.formOfEmployment,
                        };
                    });
                },
                enabled: () => salaryReviewId != null && nodeId != null && managerIdValue != null,
            });
        },
    },
};
